import { AutorenewRounded, BuildRounded, CarRental, CloudSyncRounded, FastForwardRounded, HealthAndSafety, KeyOff, MoveDownRounded, StarRounded, StoreRounded } from "@mui/icons-material";
import * as React from "react";
import { Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import CustomLoader from "../../../../util/component/CustomLoader";
import SectionHeader from "../../../../util/component/SectionHeader";
import { setSnackbar } from "../../../../util/state/globalAction";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { getBookingByIdWithMeta, getCompany } from "../../api/services";
import { CarGroupType, ReactQueryKey, RentalType, VipType, RouteParams, BookingStatus, BookingSubStatus } from "../../types";
import ActionsBar from "./actions-bar/ActionsBar";
import TabsManager from "./tabs-manager/TabsManager";
import { IFormValues } from "./types";
import { FALSE_OPTION, setDecimalSeparator, TRUE_OPTION } from "../../utils";
import { cigList } from "./recoil";

const ViewBookingPage = () => {

    const { t, i18n } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { id } = useParams<RouteParams>();

    const form = useForm<IFormValues>({
        defaultValues: {
            bookingId: {
                /* id: '',
                status: '',
                substatus: '', */
                pendingReason: '',
                salesChannelCountry: '',
                salesChannel: '',
                createdBy: '',
                creationDateTime: '',
                cancellationDateTime: '',
                expirationDateTime: '',
                oldBookingCode: '',
                updatedBy: '',
                updateDateTime: '',
                cancelledBy: '',
                bookingDateTime: ''
            },
            mainDetails: {
                pickupLocation: '',
                pickupDate: '',
                pickupTime: '',
                dropoffLocation: '',
                dropoffDate: '',
                dropoffTime: '',
                totalRentalDays: 0,
                bookedVehicleType: '',
                bookedCarGroup: '',
                carGroupModels: '',
                keyless: '',
                ancillaries: [],
                deliveryStreetName: '',
                deliveryStreetNumber: '',
                deliveryPostalCode: '',
                deliveryCity: '',
                deliveryState: '',
                collectionStreetName: '',
                collectionStreetNumber: '',
                collectionPostalCode: '',
                collectionCity: '',
                collectionState: '',
                /* delivery: '',
                deliveryStreetName: '',
                deliveryStreetNumber: '',
                deliveryPostalCode: '',
                deliveryCity: '',
                deliveryState: '',
                collection: '',
                collectionStreetName: '',
                collectionStreetNumber: '',
                collectionPostalCode: '',
                collectionCity: '',
                collectionState: '', */
                mileageIncluded: '',
                substitution: ''
            },
            otherDetails: {
                segment: '',
                category: '',
                product: '',
                rate: '',
                rateId: '',
                leisureCode: '',
                codeDescription: '',
                customerCode: '',
                cig: '',
                specialConditionsAccepted: false,
                customerAtDesk: false,
                assignedPlateNumber: '',
                flightNumber: '',
                rentwayNumber: '',
                dossierNumber: '',
                replacementPlateNumber: '',
                affiliationCode: '',
                jimpiCustomerCode: '',
                vip: VipType.No,
                fcaBankCardNumber: '',
                rentalAgreement: '',
                parentRa: '',
                greenDiscount: '',
                fastLine: '',
                discountOverprice: '',
                keyless: false
            },
            mainDriverDetails: {
                id: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
            },
            bookingOwnerDetails: {
                id: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
                companyName: '',
                country: '',
                city: '',
                streetName: '',
                streetNumber: '',
                postalCode: '',
                certifiedEmailAddress: '',
                telephone: '',
                vatNumber: '',
                vatNumberCountry: '',
                legalEntity: null,
                publicAuthority: '0',
                paIdNumber: '',
                siren: '',
                siret: '',
            },
            billingOwnerDetails: {
                id: '',
                type: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
                billingCountry: '',
                billingCity: '',
                billingStreetName: '',
                billingStreetNumber: '',
                billingPostalCode: '',
                certifiedEmailAddress: '',
                vatNumber: '',
                vatNumberCountry: '',
                invoiceCode: '',
                companyName: '',
                legalEntity: null,
                publicAuthority: '0',
                paIdNumber: '',
                siren: '',
                siret: '',
            },
            additionalDriversDetails: [],
            substitutionDetail: {
                reason: '',
                eventDate: '',
                eventPlaceCountry: '',
                eventPlaceCity: '',
                eventPlaceProvince: '',
                eventPlacePostalCode: '',
                eventPlaceStreetName: '',
                eventPlaceStreetNumber: '',
                eventDescription: '',
                vehicleAvailability: false,
                policeInvolved: false,
                notePoliceInvolved: '',
                substancesInfluence: false,
                dropoffDate:  '',
                dropoffTime: '',
                dropoffLocation: ''
            },
            subscriptionDetail: {
                dropoffDate: '',
                dropoffTime: '',
                dropoffLocation: '',
                dropoffDescLocation: ''
            }
        }
    })

    const setCigList = useSetRecoilState<string[]>(cigList);

    const bookingQuery = useQuery({
        queryKey: [ReactQueryKey.BookingById, id],
        queryFn: () => {
            if (id) {
                return getBookingByIdWithMeta(id, i18n.language)
            }
        },
        refetchOnMount: true,
        onSuccess: async (data) => {
            const booking = data?.booking;
            if (!booking) {
                return;
            }

            if(!!booking.customerCode && booking.rentalType !== RentalType.RETAIL ) {
                const result = await getCompany(
                    booking.customerCode,
                    booking.pickupDate,
                    booking.tenant,
                    booking.dealer
                );
                if (result && result.cig) {
                    setCigList(result.cig);
                } else {
                    setCigList([]);
                }
            }

            form.reset({
                bookingId: {
                    pendingReason: booking.checkBlackList ? t('label.watchlist') : booking.rules.map((r) => `${r.name}`).join(', '),
                    salesChannelCountry: booking.salesChannelCountry ?? '',
                    salesChannel: booking.salesChannel,
                    createdBy: booking.creationOperator.username,
                    creationDateTime: dayjs(booking.requestCreationDate).format('DD-MM-YYYY HH:mm'),
                    cancellationDateTime: booking.cancellationDateTime ? dayjs(booking.cancellationDateTime).format('DD-MM-YYYY HH:mm') : '',
                    expirationDateTime: booking.expirationDateTime ? dayjs(booking.expirationDateTime).format('DD-MM-YYYY HH:mm') : '',
                    oldBookingCode: booking.oldBookingCode ?? '',
                    updatedBy: booking.updateOperator?.username ?? '',
                    updateDateTime: booking.requestUpdateDate ? dayjs(booking.requestUpdateDate).format('DD-MM-YYYY HH:mm') : '',
                    cancelledBy: booking.cancellationOperator?.username ?? '',
                    bookingDateTime: dayjs(booking.bookingDatetime).format('DD-MM-YYYY HH:mm'),
                    creditType: booking.creditType,
                },
                mainDetails: {
                    pickupLocation: !(booking.status === BookingStatus.draft && booking.subStatus === BookingSubStatus.carSwap) ? booking.pickupLocation.label : '',
                    pickupDate: booking.pickupDateLocalISO ? dayjs(booking.pickupDateLocalISO).format('DD-MM-YYYY') : '',
                    pickupTime: booking.pickupDateLocalISO ? dayjs(booking.pickupDateLocalISO).format('HH:mm') : '',
                    dropoffLocation: !(booking.status === BookingStatus.draft && booking.subStatus === BookingSubStatus.carSwap) ? booking.dropoffLocation.label : '',
                    dropoffDate: booking.dropoffDateLocalISO ? dayjs(booking.dropoffDateLocalISO).format('DD-MM-YYYY') : '',
                    dropoffTime: booking.dropoffDateLocalISO ? dayjs(booking.dropoffDateLocalISO).format('HH:mm') : '',
                    totalRentalDays: booking.totalRentalDays,
                    bookedVehicleType: booking.carGroup.bookedVehicleType ? t(`vehicle_type.${booking.carGroup.bookedVehicleType}`) : '',
                    bookedCarGroup: booking.carGroup.type === CarGroupType.Acriss ? booking.carGroup.acrissCode : booking.carGroup.sippCode ?? '',
                    carGroupModels: booking.carGroup.cargroupModel,
                    keyless: booking.carGroup.keyless ? t('label.yes') : t('label.no'),
                    ancillaries: booking.ancillaries
                        .filter(a => a.selected)
                        .map(a => ({label: `${a.units > 1 ? a.units + ' x ' : ''}${a.label}`, sellType: a.sellType})),
                    // delivery: booking.delivery ? t('label.yes') : t('label.no'),
                    deliveryStreetName: booking.deliveryStreetName ?? '',
                    deliveryStreetNumber: booking.deliveryStreetNumber ?? '',
                    deliveryPostalCode: booking.deliveryPostalCode ?? '',
                    deliveryCity: booking.deliveryCity ?? '',
                    deliveryState: booking.deliveryState ?? '',
                    // collection: booking.collection ? t('label.yes') : t('label.no'),
                    collectionStreetName: booking.collectionStreetName ?? '',
                    collectionStreetNumber: booking.collectionStreetNumber ?? '',
                    collectionPostalCode: booking.collectionPostalCode ?? '',
                    collectionCity: booking.collectionCity ?? '',
                    collectionState: booking.collectionState ?? '',
                    mileageIncluded: (booking.mileageIncluded === '0KM' || booking.mileageIncluded === '0') ? 'UNLIMITED' : booking.mileageIncluded,
                    substitution: booking.substitution ? t('label.yes') : t('label.no')
                },
                otherDetails: {
                    segment: booking.segmentLabel,
                    category: booking.categoryLabel,
                    product: booking.idSmProductLabel,
                    rate: booking.rateLabel,
                    rateId: booking.rate,
                    leisureCode: booking.discountCode ?? '',
                    codeDescription: booking.codeDescription ?? '',
                    customerCode: booking.customerCode,
                    cig: booking.cig,
                    specialConditionsAccepted: booking.specialConditionsAccepted,
                    customerAtDesk: booking.customerAtDesk,
                    assignedPlateNumber: booking.assignedPlateNumber ?? '',
                    flightNumber: booking.flightNumber,
                    rentwayNumber: booking.rentwayNumber ?? '',
                    dossierNumber: booking.dossierNumber,
                    replacementPlateNumber: booking.replacementPlateNumber,
                    affiliationCode: booking.affiliationCode,
                    jimpiCustomerCode: booking.jimpiCustomerCode,
                    vip: booking.vip,
                    fcaBankCardNumber: booking.fcaBankCardNumber ?? '',
                    rentalAgreement: booking.rentalAgreement ?? '',
                    parentRa: booking.parentRa ?? '',
                    greenDiscount: booking.greenDiscount ?? '',
                    fastLine: booking.fastLine ? t('label.yes') : t('label.no'),
                    discountOverprice: booking.discountOverprice ? `${booking.discountOverprice} %` : '',
                    keyless: booking.keyless
                },
                mainDriverDetails: {
                    id: booking.firstDriver?.customerId ?? '',
                    firstName: booking.firstDriver?.name ?? '',
                    lastName: booking.firstDriver?.surname ?? '',
                    birthDate: booking.firstDriver?.birthDate ? dayjs(booking.firstDriver?.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: booking.firstDriver?.birthNation ?? '',
                    birthPlace: booking.firstDriver?.birthPlace ?? '',
                    residenceCountry: booking.firstDriver?.residenceCountry ?? '',
                    fiscalCode: booking.firstDriver?.fiscalCode ?? '',
                    drivingLicenseNumber: booking.firstDriver?.drivingLicenseNumber ?? '',
                    email: booking.firstDriver?.email ?? '',
                    mobile: booking.firstDriver?.mobile ?? '',
                    mobilePrefix: !!booking.firstDriver?.mobilePrefix?.prefix && !!booking.firstDriver?.mobilePrefix?.country ? '+' + booking.firstDriver?.mobilePrefix?.prefix  + ' (' + booking.firstDriver?.mobilePrefix?.country+ ')' : '',
                    webCheckIn: booking.firstDriver?.webCheckIn ? t('label.yes') : t('label.no'),
                    gender: booking.firstDriver?.gender ?? ''
                },
                bookingOwnerDetails: {
                    id: booking.bookingOwner?.customerId ? booking.bookingOwner.customerId : booking.bookingCompanyOwner?.customerId ? booking.bookingCompanyOwner.customerId : '',
                    firstName: booking.bookingOwner?.name ?? '',
                    lastName: booking.bookingOwner?.surname ?? '',
                    birthDate: booking.bookingOwner?.birthDate ? dayjs(booking.bookingOwner.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: booking.bookingOwner?.birthNation ?? '',
                    birthPlace: booking.bookingOwner?.birthPlace ?? '',
                    residenceCountry: booking.bookingOwner?.residenceCountry ?? '',
                    fiscalCode: booking.bookingOwner?.fiscalCode ? booking.bookingOwner.fiscalCode : booking.bookingCompanyOwner?.fiscalCode ? booking.bookingCompanyOwner.fiscalCode : '',
                    drivingLicenseNumber: booking.bookingOwner?.drivingLicenseNumber ?? '',
                    email: booking.bookingOwner?.email ? booking.bookingOwner.email : booking.bookingCompanyOwner?.email ? booking.bookingCompanyOwner.email : '',
                    mobile: booking.bookingOwner?.mobile ? booking.bookingOwner.mobile : booking.bookingCompanyOwner?.mobile ? booking.bookingCompanyOwner.mobile : '',
                    mobilePrefix: !!booking.bookingOwner?.mobilePrefix?.prefix && !!booking.bookingOwner?.mobilePrefix?.country ? '+' + booking.bookingOwner?.mobilePrefix?.prefix  + ' (' + booking.bookingOwner?.mobilePrefix?.country + ')' : !!booking.bookingCompanyOwner?.mobilePrefix?.prefix && !!booking.bookingCompanyOwner?.mobilePrefix?.country ? '+' + booking.bookingCompanyOwner?.mobilePrefix?.prefix  + ' (' + booking.bookingCompanyOwner?.mobilePrefix?.country + ')' : '',
                    companyName: booking.bookingCompanyOwner?.companyName ?? '',
                    country: booking.bookingCompanyOwner?.country ?? '',
                    city: booking.bookingCompanyOwner?.city ?? '',
                    streetName: booking.bookingCompanyOwner?.streetName ?? '',
                    streetNumber: booking.bookingCompanyOwner?.streetNumber ?? '',
                    postalCode: booking.bookingCompanyOwner?.postalCode ?? '',
                    certifiedEmailAddress: booking.bookingCompanyOwner?.pec ?? '',
                    telephone: booking.bookingCompanyOwner?.telephone ?? '',
                    sdiCode: booking.bookingCompanyOwner?.sdiCode ?? '',
                    vatNumber: booking.bookingCompanyOwner?.vatNumber ?? '',
                    vatNumberCountry: booking.bookingCompanyOwner?.vatNumberCountry ?? '',
                    legalEntity: booking.bookingCompanyOwner?.legalEntity ?? null,
                    publicAuthority: booking.bookingCompanyOwner?.publicAuthority === '1' ? t(`label.${TRUE_OPTION.label.toLocaleLowerCase()}`) : t(`label.${FALSE_OPTION.label.toLocaleLowerCase()}`),
                    paIdNumber: booking.bookingCompanyOwner?.paIdNumber ?? '',
                    siren:  booking.bookingCompanyOwner?.siren ?? '',
                    siret:  booking.bookingCompanyOwner?.siret ?? '',
                },
                billingOwnerDetails: {
                    id: booking.invoicePerson?.customerId ? booking.invoicePerson.customerId : booking.invoiceCompany?.customerId ? booking.invoiceCompany.customerId : '',
                    type: booking.invoicePerson?.type ? booking.invoicePerson.type : booking.invoiceCompany?.type ? booking.invoiceCompany.type : '',
                    firstName: booking.invoicePerson?.name ?? '',
                    lastName: booking.invoicePerson?.surname ?? '',
                    birthDate: booking.invoicePerson?.birthDate ? dayjs(booking.invoicePerson.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: booking.invoicePerson?.birthNation ?? '',
                    birthPlace: booking.invoicePerson?.birthPlace ?? '',
                    residenceCountry: booking.invoicePerson?.residenceCountry ?? '',
                    fiscalCode: booking.invoicePerson?.fiscalCode ? booking.invoicePerson.fiscalCode : booking.invoiceCompany?.fiscalCode ? booking.invoiceCompany.fiscalCode : '',
                    drivingLicenseNumber: booking.invoicePerson?.drivingLicenseNumber ?? '',
                    email: booking.invoicePerson?.email ?  booking.invoicePerson?.email : booking.invoiceCompany?.email ? booking.invoiceCompany.email : '',
                    mobile: booking.invoicePerson?.mobile ?? '',
                    mobilePrefix: !!booking.invoicePerson?.mobilePrefix?.prefix && !!booking.invoicePerson?.mobilePrefix?.country ? '+' + booking.invoicePerson?.mobilePrefix?.prefix  + ' (' + booking.invoicePerson?.mobilePrefix?.country+ ')' : '',
                    billingCountry: booking.invoicePerson?.billingCountry ? booking.invoicePerson.billingCountry : booking.invoiceCompany?.billingCountry ? booking.invoiceCompany.billingCountry : '',
                    billingCity: booking.invoicePerson?.billingCity ? booking.invoicePerson.billingCity : booking.invoiceCompany?.billingCity ? booking.invoiceCompany.billingCity : '',
                    billingStreetName: booking.invoicePerson?.billingStreetName ? booking.invoicePerson.billingStreetName : booking.invoiceCompany?.billingStreetName ? booking.invoiceCompany.billingStreetName : '',
                    billingStreetNumber: booking.invoicePerson?.billingStreetNumber ? booking.invoicePerson.billingStreetNumber : booking.invoiceCompany?.billingStreetNumber ? booking.invoiceCompany.billingStreetNumber : '',
                    billingPostalCode: booking.invoicePerson?.billingPostalCode ? booking.invoicePerson.billingPostalCode : booking.invoiceCompany?.billingPostalCode ? booking.invoiceCompany.billingPostalCode : '',
                    certifiedEmailAddress: booking.invoicePerson?.pec ? booking.invoicePerson.pec : booking.invoiceCompany?.pec ? booking.invoiceCompany.pec : '',
                    vatNumber: booking.invoiceCompany?.vatNumber ? booking.invoiceCompany.vatNumber : '',
                    vatNumberCountry: booking.invoiceCompany?.vatNumberCountry ? booking.invoiceCompany.vatNumberCountry : '',
                    invoiceCode: booking.invoiceCompany?.invoiceCode ? booking.invoiceCompany.invoiceCode : '',
                    companyName: booking.invoiceCompany?.companyName ?? '',
                    billingProvince: booking.invoicePerson?.billingProvince ? booking.invoicePerson.billingProvince : booking.invoiceCompany?.billingProvince ? booking.invoiceCompany.billingProvince : '',
                    legalEntity: booking.invoiceCompany?.legalEntity ?? null,
                    publicAuthority: booking.invoiceCompany?.publicAuthority === '1' ? t(`label.${TRUE_OPTION.label.toLocaleLowerCase()}`) : t(`label.${FALSE_OPTION.label.toLocaleLowerCase()}`),
                    paIdNumber: booking.invoiceCompany?.paIdNumber ?? '',
                    siren:  booking.invoiceCompany?.siren ?? '',
                    siret:  booking.invoiceCompany?.siret ?? '',
                    sapId: booking.invoiceCompany?.sapId,
                },
                additionalDriversDetails: booking.additionalDrivers.map(ad => ({
                    id: ad.customerId ?? '',
                    firstName: ad.name,
                    lastName: ad.surname,
                    email: ad.email,
                    webCheckIn: ad.webCheckIn ? t('label.yes') : t('label.no')
                })),
                substitutionDetail: {
                    reason: !!booking.substitutionDetail?.reason ? t(`substitution_reason.${booking.substitutionDetail?.reason}`) : '',
                    eventDate: !!booking.substitutionDetail?.eventDate ? dayjs(booking.substitutionDetail?.eventDate).format('DD-MM-YYYY HH:mm') : '',
                    eventPlaceCountry: booking.substitutionDetail?.eventPlaceCountry ?? '',
                    eventPlaceCity: booking.substitutionDetail?.eventPlaceCity ?? '',
                    eventPlaceProvince: booking.substitutionDetail?.eventPlaceProvince ?? '',
                    eventPlacePostalCode: booking.substitutionDetail?.eventPlacePostalCode ?? '',
                    eventPlaceStreetName: booking.substitutionDetail?.eventPlaceStreetName ?? '',
                    eventPlaceStreetNumber: booking.substitutionDetail?.eventPlaceStreetNumber ?? '',
                    eventDescription: booking.substitutionDetail?.eventDescription ?? '',
                    vehicleAvailability: booking.substitutionDetail?.vehicleAvailability,
                    policeInvolved: booking.substitutionDetail?.policeInvolved,
                    notePoliceInvolved: booking.substitutionDetail?.notePoliceInvolved ?? '',
                    substancesInfluence: booking.substitutionDetail?.substancesInfluence,
                    dropoffDate: !! booking.substitutionDetail?.dropoffDate ? dayjs(booking.substitutionDetail?.dropoffDate).format('DD-MM-YYYY') :'',
                    dropoffTime: !!booking.substitutionDetail?.dropoffDate ? dayjs(booking.substitutionDetail?.dropoffDate).format('HH:mm') : '',
                    dropoffLocation: booking.substitutionDetail?.dropoffLocation.label ?? ''
                },
                subscriptionDetail: {
                    dropoffDate: !!booking.dropoffDatetimeForReplacement ? dayjs(booking.dropoffDatetimeForReplacement).format('DD-MM-YYYY') :'',
                    dropoffTime: !!booking.dropoffDatetimeForReplacement ? dayjs(booking.dropoffDatetimeForReplacement).format('HH:mm') : '',
                    dropoffLocation: booking.dropoffLocationForReplacement ?? '',
                    dropoffDescLocation: booking.dropoffLocationDescForReplacement ?? ''
                }
            })
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    });

    return (
        <>
            <SectionHeader title={t('label.bookingManagement')} subtitle={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">ID:</Typography>
                        <Typography variant="bodyLarge">{id}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.owner')}:</Typography>
                        <Typography variant="bodyLarge">{bookingQuery.data?.booking.bookingOwner ? `${bookingQuery.data?.booking.bookingOwner.name} ${bookingQuery.data?.booking.bookingOwner.surname}` : bookingQuery.data?.booking.bookingCompanyOwner ? bookingQuery.data?.booking.bookingCompanyOwner.companyName : '-'}</Typography>
                    </Stack>
                    {(bookingQuery.data?.booking.bookingOwner?.customerId !== bookingQuery.data?.booking.firstDriver?.customerId) &&
                        <Stack direction="row" alignItems="end" columnGap={1}>
                            <Typography variant="bodyLarge">{t('label.driver')}:</Typography>
                            <Typography variant="bodyLarge">{bookingQuery.data?.booking.firstDriver ? `${bookingQuery.data?.booking.firstDriver.name} ${bookingQuery.data?.booking.firstDriver.surname}` : bookingQuery.data?.booking.bookingCompanyOwner ? bookingQuery.data?.booking.bookingCompanyOwner.companyName : '-'}</Typography>
                        </Stack>
                    }
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.status')}:</Typography>
                        {bookingQuery.data &&
                            <Typography variant="bodyLarge">
                                {t(`status.${bookingQuery.data.booking.status}`)}
                                {bookingQuery.data.booking.subStatus && ` - ${t(`substatus.${bookingQuery.data.booking.subStatus}`)}`}
                            </Typography>
                        }
                    </Stack>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.amount')}:</Typography>
                        <Typography variant="bodyLarge">{`${setDecimalSeparator(((bookingQuery.data?.booking.totalToBePaid ?? 0) / 100).toFixed(2), i18n.language)} €`}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.carGroup')}:</Typography>
                        <Typography variant="bodyLarge">{bookingQuery.data?.booking.carGroup.type === CarGroupType.Acriss ? bookingQuery.data?.booking.carGroup.acrissCode : bookingQuery.data?.booking.carGroup.sippCode ?? ''}</Typography>
                    </Stack>
                    <Stack direction="row" columnGap={1}>
                        {bookingQuery.data?.booking.customerAtDesk &&
                            <StoreRounded
                                titleAccess={t('label.customerAtDesk')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data && [VipType.Internal, VipType.External, VipType.VipCS].includes(bookingQuery.data.booking.vip) &&
                            <StarRounded
                                titleAccess={t('label.vip')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.fastLine &&
                            <FastForwardRounded
                                titleAccess={t('label.fastLine')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.carSwap &&
                            <CloudSyncRounded
                                titleAccess={t('label.carSwap')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.substitution &&
                            <BuildRounded
                                titleAccess={t('label.substitution')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.renewal &&
                            <AutorenewRounded
                                titleAccess={t('label.renewal')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.recovery  &&
                            <HealthAndSafety
                                titleAccess={t('label.recovery')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.packageSwap &&
                            <MoveDownRounded
                                titleAccess={t('label.packageSwap')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.keybox &&
                            <CarRental
                                titleAccess={t('label.keyBox')}
                                color="secondary"
                            />
                        }
                        {bookingQuery.data?.booking.keyless &&
                            <KeyOff
                                titleAccess={t('label.keyless')}
                                color="secondary"
                            />
                        }
                    </Stack>
                </Stack>
            } />
            <ActionsBar />
            <FormProvider {...form}>
                <TabsManager />
            </FormProvider>
            <CustomLoader loading={bookingQuery.isLoading || bookingQuery.isFetching} />
        </>
    )
}

export default ViewBookingPage;
